import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, Grid } from "@mui/material";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppSwitch from "components/Shared/AppSwitch";
import { useLazyGetDDLShipmentLoadingListQuery } from "store/services/shipmentLoadingApi";

const INITIAL_STATE = {
  loadingId: null,
  loadingBranch: null,
  unloadingBranch: null,
  isActive: true,
};

// Function to extract unique branches from trip data
const getBranchOptions = (trip) => {
  if (!trip) {
    console.warn("Trip data is undefined or null");
    return [];
  }

  const branches = new Map();

  // Add source and destination branches
  if (trip?.sourceBranch) {
    branches.set(trip.sourceBranch._id, {
      value: trip.sourceBranch._id,
      label: `${trip.sourceBranch.name} (${trip.sourceBranch.code})`,
    });
  }
  if (trip?.destinationBranch) {
    branches.set(trip.destinationBranch._id, {
      value: trip.destinationBranch._id,
      label: `${trip.destinationBranch.name} (${trip.destinationBranch.code})`,
    });
  }

  // Add branches from routes
  trip?.routes?.forEach((route) => {
    if (route.startStop) {
      branches.set(route.startStop._id, {
        value: route.startStop._id,
        label: `${route.startStop.name} (${route.startStop.code})`,
      });
    }
    if (route.endStop) {
      branches.set(route.endStop._id, {
        value: route.endStop._id,
        label: `${route.endStop.name} (${route.endStop.code})`,
      });
    }
  });

  const options = Array.from(branches.values());
  return options;
};

const TripLoadingForm = ({
  isBusy = false,
  trip = null,
  tripLoading = null,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: INITIAL_STATE,
  });

  const branchOptions = getBranchOptions(trip);

  // Fetch trip loadings
  const [fetchTripLoadings, { data: tripLoadings, isLoading: loadingTripLoadings }] =
    useLazyGetDDLShipmentLoadingListQuery();

  // Fetch loadings when trip data is available
  useEffect(() => {
    if (trip?.account?._id && trip?.entity?._id) {
      fetchTripLoadings({
        account: trip.account._id,
        entity: trip.entity._id,
      });
    }
  }, [trip, fetchTripLoadings]);

  // Pre-fill form for editing
  useEffect(() => {
    if (!tripLoading) return;
    reset({
      loadingId: tripLoading.loadingId || null,
      loadingBranch: tripLoading.loadingBranch
        ? { value: tripLoading.loadingBranch._id, label: `${tripLoading.loadingBranch.name} (${tripLoading.loadingBranch.code})` }
        : null,
      unloadingBranch: tripLoading.unLoadingBranch
        ? { value: tripLoading.unLoadingBranch._id, label: `${tripLoading.unLoadingBranch.name} (${tripLoading.unLoadingBranch.code})` }
        : null,
      isActive: tripLoading.isActive ?? true,
    });
  }, [tripLoading, reset]);

  const onFormSubmit = async (data) => {
    const payload = {
      loadingId: data.loadingId?._id || null,
      loadingBranch: data.loadingBranch?.value || null,
      unLoadingBranch: data.unloadingBranch?.value || null,
      isActive: data.isActive,
    };
    onSubmit(payload);
    reset(INITIAL_STATE);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} padding={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <Controller
            name="loadingId"
            control={control}
            rules={{ required: "Please select a Loading ID." }}
            render={({ field: { value, onChange } }) => (
              <BaseAutocomplete
                fullWidth
                label="Loading ID"
                required
                value={value ?? null}
                onChange={(_, data) => onChange(data)}
                getOptionLabel={(option) => option?.loadingId || ""}
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id || value === null
                }
                options={tripLoadings || []}
                loading={loadingTripLoadings}
                loadingText="Loading Trip Loadings..."
                helperText={errors?.loadingId?.message}
                error={!!errors?.loadingId}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Controller
            name="loadingBranch"
            control={control}
            rules={{ required: "Please select a Loading Branch." }}
            render={({ field: { value, onChange } }) => (
              <BaseAutocomplete
                fullWidth
                label="Loading Branch"
                required
                value={value ?? null}
                onChange={(_, data) => onChange(data)}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value || value === null
                }
                options={branchOptions || []}
                helperText={errors?.loadingBranch?.message}
                error={!!errors?.loadingBranch}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Controller
            name="unloadingBranch"
            control={control}
            rules={{ required: "Please select an Unloading Branch." }}
            render={({ field: { value, onChange } }) => (
              <BaseAutocomplete
                fullWidth
                label="Unloading Branch"
                required
                value={value ?? null}
                onChange={(_, data) => onChange(data)}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value || value === null
                }
                options={branchOptions || []}
                helperText={errors?.unloadingBranch?.message}
                error={!!errors?.unloadingBranch}
              />
            )}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <AppSwitch {...field} label="Is Active?" />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={isBusy || loadingTripLoadings}>
              Submit
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TripLoadingForm;
